import React, { useState } from 'react';
import { Search, Calendar, Filter, Download, CheckCircle, Clock, Truck, Building, ChevronDown, ChevronUp, FileText, MapPin, User } from 'lucide-react';
interface CompletedJob {
  id: string;
  date: string;
  completedDate: string;
  customer: string;
  jobName: string;
  jobNumber: string;
  material: string;
  truckType: string;
  loadLocation: {
    name: string;
  };
  dumpLocation: {
    name: string;
  };
  foreman: {
    name: string;
    signature?: boolean;
  };
  stats: {
    loads: number;
    tonnage: number;
    hours: number;
    startTime: string;
    endTime: string;
    truckHours?: number | null;
  };
  status: 'completed';
  ticketNumber: string;
  paymentStatus: 'paid' | 'pending' | 'processing';
  loadsDetails?: any[]; // Add this to store individual load information
  comments?: any[]; // Add this to store comments
}
interface CompletedJobsProps {
  jobs: CompletedJob[];
}
export const CompletedJobs = ({
  jobs
}: CompletedJobsProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDateFilter, setShowCustomDateFilter] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [customerFilter, setCustomerFilter] = useState('all');
  // Get unique customers for filter dropdown
  const customers = ['all', ...new Set(jobs.map(job => job.customer))];
  // Filter jobs based on search term, date filter, and customer filter
  const filteredJobs = jobs.filter(job => {
    // Search term filter
    const searchMatch = job.jobName.toLowerCase().includes(searchTerm.toLowerCase()) || job.jobNumber.toLowerCase().includes(searchTerm.toLowerCase()) || job.customer.toLowerCase().includes(searchTerm.toLowerCase()) || job.ticketNumber.toLowerCase().includes(searchTerm.toLowerCase());
    // Date filter
    let dateMatch = true;
    const jobDate = new Date(job.completedDate);
    const today = new Date();
    if (dateFilter === '7days') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 7);
      dateMatch = jobDate >= sevenDaysAgo;
    } else if (dateFilter === '30days') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      dateMatch = jobDate >= thirtyDaysAgo;
    } else if (dateFilter === '90days') {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(today.getDate() - 90);
      dateMatch = jobDate >= ninetyDaysAgo;
    } else if (dateFilter === 'custom' && customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999); // Include the entire end day
      dateMatch = jobDate >= startDate && jobDate <= endDate;
    }
    // Customer filter
    const customerMatch = customerFilter === 'all' || job.customer === customerFilter;
    return searchMatch && dateMatch && customerMatch;
  });
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  const toggleJobExpansion = (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
    } else {
      setExpandedJobId(jobId);
    }
  };
  const handleDownloadTimecard = (jobId: string) => {
    // In a real app, this would download a PDF of the timecard
    alert(`Downloading timecard for job ID: ${jobId}`);
  };
  // Add a function to view load details
  const handleViewLoadDetails = (job: CompletedJob) => {
    if (job.loadsDetails && job.loadsDetails.length > 0) {
      const loadDetails = job.loadsDetails.map((load, index) => `Load #${index + 1}:\n` + `Ticket: ${load.ticket}\n` + `Tonnage: ${load.tons} tons\n` + `Load Time: ${load.loadInTime} - ${load.loadOutTime}\n` + `Dump Time: ${load.dumpInTime} - ${load.dumpOutTime}\n`).join('\n\n');
      alert(`Load Details for ${job.jobName}:\n\n${loadDetails}`);
    } else {
      alert('No detailed load information available for this job.');
    }
  };
  return <div className="space-y-4" data-id="element-2114">
      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4" data-id="element-2115">
        <div className="flex flex-col md:flex-row gap-3 mb-4" data-id="element-2116">
          {/* Search */}
          <div className="flex-1" data-id="element-2117">
            <div className="relative" data-id="element-2118">
              <input type="text" placeholder="Search by job name, number, or customer..." className="w-full pl-10 pr-4 py-2 border rounded-md" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2119" />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} data-id="element-2120" />
            </div>
          </div>
          {/* Date Filter */}
          <div className="w-full md:w-48" data-id="element-2121">
            <div className="relative" data-id="element-2122">
              <select className="w-full pl-10 pr-4 py-2 border rounded-md appearance-none" value={dateFilter} onChange={e => {
              setDateFilter(e.target.value);
              setShowCustomDateFilter(e.target.value === 'custom');
            }} data-id="element-2123">
                <option value="all" data-id="element-2124">All Dates</option>
                <option value="7days" data-id="element-2125">Last 7 Days</option>
                <option value="30days" data-id="element-2126">Last 30 Days</option>
                <option value="90days" data-id="element-2127">Last 90 Days</option>
                <option value="custom" data-id="element-2128">Custom Range</option>
              </select>
              <Calendar className="absolute left-3 top-2.5 text-gray-400" size={18} data-id="element-2129" />
            </div>
          </div>
          {/* Customer Filter */}
          <div className="w-full md:w-48" data-id="element-2130">
            <div className="relative" data-id="element-2131">
              <select className="w-full pl-10 pr-4 py-2 border rounded-md appearance-none" value={customerFilter} onChange={e => setCustomerFilter(e.target.value)} data-id="element-2132">
                <option value="all" data-id="element-2133">All Customers</option>
                {customers.filter(c => c !== 'all').map(customer => <option key={customer} value={customer} data-id="element-2134">
                      {customer}
                    </option>)}
              </select>
              <Building className="absolute left-3 top-2.5 text-gray-400" size={18} data-id="element-2135" />
            </div>
          </div>
        </div>
        {/* Custom Date Range */}
        {showCustomDateFilter && <div className="flex flex-col sm:flex-row gap-3 mt-3 p-3 bg-gray-50 rounded-md" data-id="element-2136">
            <div className="flex-1" data-id="element-2137">
              <label className="block text-sm text-gray-600 mb-1" data-id="element-2138">
                Start Date
              </label>
              <input type="date" className="w-full px-3 py-2 border rounded-md" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} data-id="element-2139" />
            </div>
            <div className="flex-1" data-id="element-2140">
              <label className="block text-sm text-gray-600 mb-1" data-id="element-2141">
                End Date
              </label>
              <input type="date" className="w-full px-3 py-2 border rounded-md" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} data-id="element-2142" />
            </div>
            <div className="flex items-end" data-id="element-2143">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => {
            if (customStartDate && customEndDate) {
              setDateFilter('custom');
            }
          }} data-id="element-2144">
                Apply
              </button>
            </div>
          </div>}
      </div>
      {/* Results Count */}
      <div className="text-sm text-gray-600" data-id="element-2145">
        Showing {filteredJobs.length} of {jobs.length} completed jobs
      </div>
      {/* Job List */}
      {filteredJobs.length === 0 ? <div className="bg-white rounded-lg shadow p-6 text-center" data-id="element-2146">
          <FileText size={40} className="mx-auto text-gray-400 mb-2" data-id="element-2147" />
          <p className="text-gray-600" data-id="element-2148">
            No completed jobs found matching your filters
          </p>
        </div> : <div className="space-y-4" data-id="element-2149">
          {filteredJobs.map(job => <div key={job.id} className="bg-white rounded-lg shadow overflow-hidden" data-id="element-2150">
              {/* Job Header */}
              <div className="p-4 bg-gray-50 border-b" data-id="element-2151">
                <div className="flex justify-between items-start" data-id="element-2152">
                  <div data-id="element-2153">
                    <div className="flex items-center gap-2" data-id="element-2154">
                      <h3 className="font-semibold text-lg" data-id="element-2155">{job.jobName}</h3>
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-2156">
                        <CheckCircle size={12} data-id="element-2157" />
                        Completed
                      </span>
                      {job.paymentStatus === 'paid' && <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full" data-id="element-2158">
                          Paid
                        </span>}
                      {job.paymentStatus === 'pending' && <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full" data-id="element-2159">
                          Payment Pending
                        </span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mt-1" data-id="element-2160">
                      <span data-id="element-2161">Job #{job.jobNumber}</span>
                      <span data-id="element-2162">•</span>
                      <span data-id="element-2163">Ticket #{job.ticketNumber}</span>
                    </div>
                  </div>
                  <button onClick={() => toggleJobExpansion(job.id)} className="text-gray-500 hover:text-gray-700" aria-label={expandedJobId === job.id ? 'Collapse job details' : 'Expand job details'} data-id="element-2164">
                    {expandedJobId === job.id ? <ChevronUp size={20} data-id="element-2165" /> : <ChevronDown size={20} data-id="element-2166" />}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2" data-id="element-2167">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-2168">
                    <Calendar size={12} data-id="element-2169" />
                    {formatDate(job.completedDate)}
                  </span>
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-2170">
                    <Clock size={12} data-id="element-2171" />
                    {job.stats.hours.toFixed(1)} hrs
                  </span>
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-2172">
                    <Truck size={12} data-id="element-2173" />
                    {job.truckType}
                  </span>
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full" data-id="element-2174">
                    {job.material}
                  </span>
                </div>
              </div>
              {/* Customer Info - Always visible */}
              <div className="p-4 border-b" data-id="element-2175">
                <div className="flex items-start gap-2" data-id="element-2176">
                  <Building size={18} className="text-gray-400 mt-0.5" data-id="element-2177" />
                  <div data-id="element-2178">
                    <p className="text-sm text-gray-500" data-id="element-2179">Customer</p>
                    <p className="font-medium" data-id="element-2180">{job.customer}</p>
                  </div>
                </div>
              </div>
              {/* Job Details (expanded) */}
              {expandedJobId === job.id && <div className="p-4" data-id="element-2181">
                  {/* Job Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-id="element-2182">
                    <div className="bg-gray-50 p-3 rounded-md" data-id="element-2183">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-2184">Total Loads</p>
                      <p className="text-lg font-medium" data-id="element-2185">{job.stats.loads}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-md" data-id="element-2186">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-2187">
                        Total Tonnage
                      </p>
                      <p className="text-lg font-medium" data-id="element-2188">
                        {job.stats.tonnage.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-md" data-id="element-2189">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-2190">Hours Worked</p>
                      <p className="text-lg font-medium" data-id="element-2191">
                        {job.stats.hours.toFixed(1)}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-md" data-id="element-2192">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-2193">Time Period</p>
                      <p className="text-sm font-medium" data-id="element-2194">
                        {job.stats.startTime} - {job.stats.endTime}
                      </p>
                    </div>
                  </div>
                  {/* Truck Hours (if available) */}
                  {job.stats.truckHours != null && <div className="mb-4 bg-blue-50 p-3 rounded-md" data-id="element-2195">
                      <div className="flex items-center gap-2" data-id="element-2196">
                        <ClockIcon size={18} className="text-blue-600" data-id="element-2197" />
                        <div data-id="element-2198">
                          <p className="text-sm font-medium" data-id="element-2199">Truck Hours</p>
                          <p className="text-base" data-id="element-2200">
                            {job.stats.truckHours.toFixed(1)} hours
                          </p>
                        </div>
                      </div>
                    </div>}
                  {/* Load Details Button */}
                  {job.loadsDetails && job.loadsDetails.length > 0 && <div className="mb-4" data-id="element-2201">
                      <button onClick={() => handleViewLoadDetails(job)} className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded flex items-center justify-center gap-2" data-id="element-2202">
                        <TruckIcon size={16} data-id="element-2203" />
                        View {job.stats.loads} Load Details
                      </button>
                    </div>}
                  {/* Locations */}
                  <div className="mb-4" data-id="element-2204">
                    <h4 className="font-medium mb-2 text-sm" data-id="element-2205">Locations</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2206">
                      <div className="flex items-start gap-2" data-id="element-2207">
                        <MapPin size={18} className="text-blue-500 mt-0.5" data-id="element-2208" />
                        <div data-id="element-2209">
                          <p className="text-sm font-medium" data-id="element-2210">Load Location</p>
                          <p className="text-sm text-gray-600" data-id="element-2211">
                            {job.loadLocation.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2" data-id="element-2212">
                        <MapPin size={18} className="text-green-500 mt-0.5" data-id="element-2213" />
                        <div data-id="element-2214">
                          <p className="text-sm font-medium" data-id="element-2215">Dump Location</p>
                          <p className="text-sm text-gray-600" data-id="element-2216">
                            {job.dumpLocation.name}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Foreman Verification */}
                  <div className="mb-4" data-id="element-2217">
                    <h4 className="font-medium mb-2 text-sm" data-id="element-2218">
                      Foreman Verification
                    </h4>
                    <div className="flex items-start gap-2" data-id="element-2219">
                      <User size={18} className="text-gray-500 mt-0.5" data-id="element-2220" />
                      <div data-id="element-2221">
                        <p className="text-sm font-medium" data-id="element-2222">
                          {job.foreman.name}
                        </p>
                        <div className="mt-1" data-id="element-2223">
                          {job.foreman.signature ? <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full" data-id="element-2224">
                              <CheckCircle size={12} data-id="element-2225" />
                              Signature Verified
                            </span> : <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full" data-id="element-2226">
                              <Clock size={12} data-id="element-2227" />
                              Signature Skipped
                            </span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Comments (if available) */}
                  {job.comments && job.comments.length > 0 && <div className="mb-4" data-id="element-2228">
                      <h4 className="font-medium mb-2 text-sm" data-id="element-2229">Comments</h4>
                      <div className="bg-gray-50 p-3 rounded-md" data-id="element-2230">
                        <p className="text-sm text-gray-700" data-id="element-2231">
                          {job.comments.length} comment(s) attached to this job
                        </p>
                        <button onClick={() => {
                const commentText = job.comments?.map((comment, index) => `Comment #${index + 1}: ${comment.text} (${comment.timestamp})`).join('\n\n');
                alert(`Comments for ${job.jobName}:\n\n${commentText}`);
              }} className="mt-2 text-blue-600 text-sm" data-id="element-2232">
                          View Comments
                        </button>
                      </div>
                    </div>}
                </div>}
              {/* Action Footer */}
              <div className="p-3 bg-gray-50 border-t flex justify-between items-center" data-id="element-2233">
                <div className="text-sm text-gray-500" data-id="element-2234">
                  Completed on {formatDate(job.completedDate)}
                </div>
                <button onClick={() => handleDownloadTimecard(job.id)} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm" data-id="element-2235">
                  <Download size={16} data-id="element-2236" />
                  Download Timecard
                </button>
              </div>
            </div>)}
        </div>}
    </div>;
};